/**
 * Medical Director Dashboard Component
 * Displays provider watch lists and performance metrics
 * Issue #77 - Medical Director Dashboard Implementation
 */

import { 
  Table, 
  Button, 
  Typography, 
  Card
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { 
  MedicalDirectorData, 
  MedicalDirectorColumn,
  MedicalDirectorDateValue,
  MedicalDirectorMetricValue
} from '../types/dashboard.types';

const { Title, Text } = Typography;

interface MedicalDirectorProvider {
  id: number;
  name: string;
  is_watched: boolean;
  assigned_to: Array<{ facility_id: number }>;
  med_dash_date_values?: MedicalDirectorDateValue[];
  med_dash_metric_values?: MedicalDirectorMetricValue[];
  has_values?: boolean;
}


interface MedicalDirectorDashboardProps {
  data: MedicalDirectorData;
  loading?: boolean;
}

function MedicalDirectorDashboard({ 
  data, 
  loading = false 
}: MedicalDirectorDashboardProps) {
  // Type-safe data extraction
  const providers = (data.providers || []) as MedicalDirectorProvider[];
  const dateColumns = (data.med_dash_date_columns || []) as MedicalDirectorColumn[];
  const metricColumns = (data.med_dash_metric_columns || []) as MedicalDirectorColumn[];
  
  // Filter watched providers (only they have column data)
  const watchedProviders = providers.filter(p => p.is_watched);

  // Handle go to employee navigation
  const handleGoToEmployee = (providerId: number) => {
    // Navigate to employee detail page (AngularJS route)
    window.location.href = `/hr/employees#/employees/${providerId}`;
  };

  // Helper function to render date value with background color
  const renderDateValue = (dateValue: MedicalDirectorDateValue | undefined) => {
    if (!dateValue || !dateValue.latest_date) return null;
    
    const style = dateValue.severity ? { backgroundColor: dateValue.severity } : {};
    const date = new Date(dateValue.latest_date);
    const formattedDate = date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
    
    return (
      <div style={{ ...style, padding: '8px', borderRadius: '4px' }}>
        {formattedDate}
      </div>
    );
  };

  // Helper function to render metric value with background color
  const renderMetricValue = (metricValue: MedicalDirectorMetricValue | undefined) => {
    if (!metricValue || !metricValue.subvalues || metricValue.subvalues.length === 0) return null;
    
    const style = metricValue.severity ? { backgroundColor: metricValue.severity } : {};
    
    return (
      <div style={{ ...style, padding: '8px', borderRadius: '4px' }}>
        {metricValue.subvalues.map((subvalue, index) => (
          <div key={index} style={{ marginBottom: index < metricValue.subvalues.length - 1 ? '4px' : 0 }}>
            {subvalue.label !== 'Other' && <span>{subvalue.label}: </span>}
            {subvalue.value}
          </div>
        ))}
      </div>
    );
  };

  // Create dynamic columns for date values table
  const createDateColumnsConfig = (): ColumnsType<MedicalDirectorProvider> => {
    const baseColumns: ColumnsType<MedicalDirectorProvider> = [
      {
        title: 'Provider',
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        width: 200,
        render: (name: string) => <Text strong>{name}</Text>
      }
    ];

    // Add dynamic date columns
    dateColumns.forEach((column) => {
      baseColumns.push({
        title: column.title,
        key: `date_${column.template_name}`,
        width: 150,
        render: (_, record) => {
          const dateValue = record.med_dash_date_values?.find(
            (v: MedicalDirectorDateValue) => v.template_name === column.template_name
          );
          return renderDateValue(dateValue);
        }
      });
    });

    // Add actions column
    baseColumns.push({
      title: '',
      key: 'actions',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => handleGoToEmployee(record.id)}
        >
          Go to Employee
        </Button>
      )
    });

    return baseColumns;
  };

  // Create dynamic columns for metric values table  
  const createMetricColumnsConfig = (): ColumnsType<MedicalDirectorProvider> => {
    const baseColumns: ColumnsType<MedicalDirectorProvider> = [
      {
        title: 'Provider',
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        width: 200,
        render: (name: string) => <Text strong>{name}</Text>
      }
    ];

    // Add dynamic metric columns
    metricColumns.forEach((column) => {
      baseColumns.push({
        title: column.title,
        key: `metric_${column.template_name}`,
        width: 150,
        render: (_, record) => {
          const metricValue = record.med_dash_metric_values?.find(
            (v: MedicalDirectorMetricValue) => v.template_name === column.template_name
          );
          return renderMetricValue(metricValue);
        }
      });
    });

    // Add actions column
    baseColumns.push({
      title: '',
      key: 'actions',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => handleGoToEmployee(record.id)}
        >
          Go to Employee
        </Button>
      )
    });

    return baseColumns;
  };

  return (
    <div style={{ padding: '20px' }}>
      <Title level={2}>Medical Director Dashboard</Title>
      


      {/* Date Values Table - Only show if there are date columns and watched providers */}
      {dateColumns.length > 0 && watchedProviders.length > 0 && (
        <Card title="Date Tracking" style={{ marginBottom: '24px' }}>
          <Table
            columns={createDateColumnsConfig()}
            dataSource={watchedProviders}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={{ x: Math.max(800, 200 + dateColumns.length * 150 + 150) }}
            size="small"
          />
        </Card>
      )}

      {/* Metric Values Table - Only show if there are metric columns and watched providers with values */}
      {metricColumns.length > 0 && watchedProviders.some(p => p.has_values) && (
        <Card title="Performance Metrics" style={{ marginBottom: '24px' }}>
          <Table
            columns={createMetricColumnsConfig()}
            dataSource={watchedProviders.filter(p => p.has_values)}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={{ x: Math.max(800, 200 + metricColumns.length * 150 + 150) }}
            size="small"
          />
        </Card>
      )}



    </div>
  );
}

export default MedicalDirectorDashboard;